<template>
  <div>
    <b-overlay
      :show="recipeGroupsResponses('recipesGroupsList') && recipesGroupsEmpty"
      spinner-type="border"
      variant="light"
      spinner-variant="primary"
      opacity="1"
      rounded>
      <div class="d-flex align-items-end pt-2 pb-6">
        <div class="d-flex flex-column">
          <h1 class="h5 mb-2">
            Recipe Groups
          </h1>
          <b-breadcrumb class="breadcrumb-transparent breadcrumb-line p-0 m-0">
            <b-breadcrumb-item :to="{ name: 'Dashboard' }">
              <i class="fa fa-home fa-sm mr-2"></i>
              Home
            </b-breadcrumb-item>
            <b-breadcrumb-item active>Settings</b-breadcrumb-item>
            <b-breadcrumb-item active>Recipe Groups</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
        <div class="ml-auto">
          <b-button
            size="sm"
            v-wave
            variant="primary"
            class="align-middle btn-shadow"
            :disabled="recipeGroupsResponses('manageRecipeGroup')"
            @click="unselectRecipeGroup(), showModal('manageModal')">
            <i class="fa fa-plus fa-sm p-0 pr-sm-2"></i>
            <span class="d-none d-sm-inline">Add New Recipe Group</span>
          </b-button>
        </div>
      </div>

      <b-card
        no-body
        class="card-custom card-border">

        <b-card-body>
          <b-table
            :items="recipesGroupsList"
            :fields="fields"
            :class="{ 'table-hover' : !recipesGroupsEmpty }"
            stacked="sm"
            head-variant=""
            show-empty
            responsive
            class="mb-0">
            <template #cell(order)="data">
              <span class="small font-weight-light">{{ data.index + 1 }}</span>
            </template>
            <template #cell(edit)="data">
              <b-button
                size="sm"
                v-wave
                variant="outline-primary"
                class="mr-4 align-middle"
                @click="
                  data.toggleDetails(),
                  getRecipeGroupRecipes(data.item._id, data.detailsShowing)
                ">
                <i
                  class="fas fa-sm"
                  :class="data.detailsShowing ? 'fa-chevron-up' : 'fa-chevron-down'" />
                <span v-if="!data.detailsShowing">
                  Show Recipes
                  ({{ data.item.recipes }})
                </span>
                <span v-else>Hide Recipes</span>
              </b-button>
              <b-button
                size="sm"
                v-wave
                variant="outline-primary"
                class="mr-4 align-middle"
                :disabled="recipeGroupsResponses('manageRecipeGroup')"
                @click="selectRecipeGroup(data.item), showModal('manageModal')">
                <i class="fa fa-pen fa-sm"></i>
                <span>Edit</span>
              </b-button>
              <b-button
                :id="`deleteButton-${data.item._id}`"
                v-wave
                size="sm"
                variant="outline-primary"
                class="btn-icon"
                :disabled="recipeGroupsResponses('deleteRecipeGroup')"
                @click="selectRecipeGroup(data.item), showDeletePopover(data.item._id)">
                <i class="fa fa-trash fa-sm"></i>
              </b-button>
            </template>
            <template #row-details="row">
              <recipes-groups-details :data="row" />
            </template>
            <template #empty="scope">
              <no-data-found />
            </template>
          </b-table>
        </b-card-body>
      </b-card>
    </b-overlay>

    <b-popover
      v-for="group in recipesGroupsList"
      :key="`popover-${group._id}`"
      :target="`deleteButton-${group._id}`"
      :ref="`popover-${group._id}`"
      triggers="manual blur"
      placement="left">
      <p>Are you sure you want to delete «<span class="font-weight-bolder">{{ group.name }}</span>» group?</p>
      <div class="d-flex w-100">
        <b-button
          @click="hideDeletePopover(group._id)"
          variant="outline-primary"
          size="sm">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          size="sm"
          class="ml-4 text-center justify-content-center flex-grow-1"
          @click="confirmDelete(group._id), hideDeletePopover(group._id)">
          <i class="fa fa-check fa-sm"></i>
          <span>Yes, delete</span>
        </b-button>
      </div>
    </b-popover>

    <b-modal
      ref="manageModal"
      centered
      size="md"
      :title="recipesGroupsModalTitle"
      hide-header-close
      header-class="py-4 border-bottom-0"
      body-class="pt-4"
      hide-footer
      @hidden="unselectRecipeGroup(), clearErrors()">
      <b-form @submit.stop.prevent>
        <b-form-group
          id="name-group"
          label="Name"
          label-for="name-input">
          <b-form-input
            id="name-input"
            name="name-input"
            v-model="selectedRecipeGroup.name"
            :state="getValidationState('name')"
            aria-describedby="name-feedback"
            placeholder="Default" />
          <b-form-invalid-feedback
            v-for="(error, index) in manageRecipeGroupError('name')"
            :key="`${error.field}-${index}`">
            {{ error.message }}
          </b-form-invalid-feedback>
        </b-form-group>

        <div class="d-flex w-100 pt-2">
          <b-button
            variant="outline-secondary"
            size="md"
            @click="hideModal('manageModal')">
            Cancel
          </b-button>
          <b-button
            type="submit"
            v-wave
            variant="primary"
            size="md"
            class="ml-auto"
            @click="submitRecipeGroup()">
            Submit
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <modal-new-recipe />
  </div>
</template>

<script>
import {
  // actions
  GET_RECIPES_GROUPS,
  GET_RECIPE_GROUP,
  MANAGE_RECIPE_GROUP,
  DELETE_RECIPE_GROUP,
  GET_RECIPE_GROUP_RECIPES,

  // mutations
  SELECT_RECIPE_GROUP,
  UNSELECT_RECIPE_GROUP,
  CLEAR_RECIPES_GROUPS,
  CLEAR_MANAGE_RECIPE_GROUP_ERRORS,
} from '@/core/services/store/recipes-groups.module'
import { mapGetters } from 'vuex'
import { eventBus } from '@/main'
import {
  showToast,
  showModal,
  hideModal,
  showDeletePopover,
  hideDeletePopover,
} from '@/core/utils'
import RecipesGroupsDetails from './components/RecipesGroupsDetails.vue'
import ModalNewRecipe from './components/ModalNewRecipe.vue'

export default {
  name: 'SettingsRecipesGroups',
  components: {
    RecipesGroupsDetails,
    ModalNewRecipe,
  },
  data() {
    return {
      fields: [
        {
          label: '#',
          key: 'order',
          class: ['align-middle', 'pl-5'],
          thStyle: 'width: 50px;'
        },
        {
          label: 'Name',
          key: 'name',
          class: ['align-middle', 'font-weight-bold'],
          thStyle: 'width: 30%'
        },
        {
          label: 'Servers',
          key: 'servers',
          class: ['align-middle', 'text-left text-md-right'],
        },
        {
          label: 'Recipes',
          key: 'recipes',
          class: ['align-middle', 'text-left text-md-right'],
        },
        {
          label: 'Actions',
          key: 'edit',
          class: ['align-middle', 'text-md-right', 'text-nowrap', 'pr-5', 'pl-10'],
        }
      ],

      showToast,
      showModal,
      hideModal,
      showDeletePopover,
      hideDeletePopover,
    }
  },
  computed: {
    ...mapGetters([
      'recipesGroupsList',
      'recipesGroupsEmpty',
      'totalRecipesGroups',
      'selectedRecipeGroup',
      'selectedRecipeGroupId',
      'recipesGroupsModalTitle',
      'manageRecipeGroupErrors',
      'manageRecipeGroupError',
      'recipeGroupsResponses',
    ]),
  },
  created() {
    this.clearRecipeGroups()
    this.getRecipeGroups('start')

    eventBus.$on('showToast/SettingsRecipesGroups', ({ message, variant }) => {
      this.showToast(message, variant)
    })
  },
  destroyed() {
    this.clearRecipeGroups()

    eventBus.$off('showToast/SettingsRecipesGroups')
  },
  methods: {
    submitRecipeGroup() {
      this.$store
        .dispatch(MANAGE_RECIPE_GROUP)
        .then(() => {
          if (this.selectedRecipeGroupId) {
            this.getRecipeGroup()
          } else {
            this.getRecipeGroups('refresh')
          }
        })
        .then(() => {
          this.hideModal('manageModal')
        })
    },

    getRecipeGroup() {
      this.$store.dispatch(GET_RECIPE_GROUP, {
        id: this.selectedRecipeGroupId
      })
    },

    getRecipeGroups(action) {
      this.$store.dispatch(GET_RECIPES_GROUPS, { action })
    },

    clearRecipeGroups() {
      this.$store.commit(CLEAR_RECIPES_GROUPS)
    },

    selectRecipeGroup(group) {
      this.$store.commit(SELECT_RECIPE_GROUP, group)
    },

    unselectRecipeGroup() {
      this.$store.commit(UNSELECT_RECIPE_GROUP)
    },

    getRecipeGroupRecipes(id, isShowing) {
      this.$store.dispatch(GET_RECIPE_GROUP_RECIPES, { id, isShowing })
    },

    clearErrors() {
      this.$store.commit(CLEAR_MANAGE_RECIPE_GROUP_ERRORS)
    },

    getValidationState(field) {
      return this.manageRecipeGroupErrors.some(err => err.field == field) ? false : null
    },

    confirmDelete(id) {
      this.$store
        .dispatch(DELETE_RECIPE_GROUP, { id })
        .then(() => {
          this.getRecipeGroups('start')
        })
    },
  }
}
</script>
